/**
 * Lender profile resolution + consent copy. Dependency-free so node:test can cover it
 * (`node --test src/lib/lenderResolveCore.test.js`); the React side is `useLender` in lender.js.
 *
 * Two kinds of caller:
 *  - NO lender key (pre-journey screens): they get the configured primary lender, as always.
 *  - An EXPLICIT lender key (the journey's own lender): they get that lender, or — when that
 *    lender has no usable identity (no legalName/shortCode, e.g. a lender declared before its
 *    kit arrives) — a NEUTRAL profile. Never another lender: consent copy that names a lender
 *    the journey is not with is wrong, and it is recorded as the tenant's consent.
 */
import {
  DEFAULT_LENDER_PROFILES,
  DEFAULT_PRIMARY_LENDER,
} from "./lenderDefaults.js";

/** What consent / legal copy says when the journey's lender has no usable identity. */
export const NEUTRAL_LENDER_PHRASE = "our lending partner";

/** The label a consent is recorded under when no lender name may be shown. */
export const NEUTRAL_LENDER_LABEL = "Lender";

/** True for the neutral profile `resolveLender` returns instead of substituting a lender. */
export const isNeutralLender = (profile) => Boolean(profile?.neutral);

/**
 * How the apply sentence names the lender. A real lender: `Legal Name (“CODE”/ “Lender”)`,
 * exactly as before. The neutral profile: `our lending partner (“Lender”)`.
 */
export function lenderNamePhrase(lender) {
  if (isNeutralLender(lender)) return `${NEUTRAL_LENDER_PHRASE} (“Lender”)`;
  return `${lender.legalName} (“${lender.shortCode}”/ “Lender”)`;
}

/** The label the apply consent is recorded under: the lender's legal name, or a neutral one. */
export const lenderConsentLabel = (lender) =>
  isNeutralLender(lender) ? NEUTRAL_LENDER_LABEL : lender.legalName;

/** The short name used in the CKYC and AA sentences. */
const lenderShortName = (lender) =>
  isNeutralLender(lender) ? NEUTRAL_LENDER_PHRASE : lender.shortCode;

// Builds lender-specific consent copy. The legal sentence templates live here
// (compliance-controlled); only the lender's name / short code are injected, so
// FEXPRIME renders exactly as before and FINTREE gets correct copy.
export function buildConsents(lender) {
  return {
    apply: `I hereby apply the RENT FINANCING with ${lenderNamePhrase(lender)} as per the details provided by me and I confirm to have read, understood and agreed to the PRODUCT DETAILS and other relevant APPLICATION TERMS.`,
    ckyc: `I hereby authorize and give my consent to ${lenderShortName(lender)} to download my KYC records from the Central KYC Registry (CKYCR) for the purpose of verification of my identity and address from the CKYCR database`,
    banking: `By continuing, I hereby agree to AA Terms of Service and authorize ${lenderShortName(lender)} to access my banking data via the RBI-licensed Account Aggregator network to assess credit eligibility`,
  };
}

// The two strings interpolated into consent copy and shown beside the logo.
const hasLegalIdentity = (profile) =>
  Boolean(profile?.legalName && profile?.shortCode);

// The hrefs behind "PRODUCT DETAILS" / "AA Terms of Service" in the FEXPRIME banking
// ConsentDialog. Preferred, never required: a lender whose links are still awaited
// (GROMOR) must render as itself, not as whichever lender happens to be primary.
const hasConsentLinks = (profile) =>
  Boolean(profile?.productUrl && profile?.termsUrl);

const isComplete = (profile) =>
  hasLegalIdentity(profile) && hasConsentLinks(profile);

const explicitKey = (key) =>
  typeof key === "string" && key.trim() ? key.trim() : null;

function neutralProfile(key) {
  const profile = {
    key,
    legalName: "",
    shortCode: "",
    productUrl: "",
    termsUrl: "",
    homeUrl: "",
    logoUrl: "",
    neutral: true,
  };
  return { ...profile, consents: buildConsents(profile) };
}

// Resolves a lender key to its profile (+ consents). The requested lender is tried
// first in both its live (admin-overlaid) and baked-in forms, preferring the copy that
// still has its consent links — so an ops edit that blanks a link falls back to that
// SAME lender's defaults, exactly as before. The primary lender is reached only when
// NO key is given; an explicit key without a usable identity gets the neutral profile.
export function resolveLender(lenderConfig, key) {
  const lenders = lenderConfig?.lenders || DEFAULT_LENDER_PROFILES;
  const primary = lenderConfig?.primaryLender || DEFAULT_PRIMARY_LENDER;
  const requested = explicitKey(key);
  const resolvedKey = requested || primary;
  const own = [lenders[resolvedKey], DEFAULT_LENDER_PROFILES[resolvedKey]];
  const ownProfile = own.find(isComplete) || own.find(hasLegalIdentity);
  if (requested && !ownProfile) return neutralProfile(requested);
  const fallback = [
    lenders[primary],
    DEFAULT_LENDER_PROFILES[DEFAULT_PRIMARY_LENDER],
  ];
  const profile =
    ownProfile ||
    fallback.find(isComplete) ||
    fallback.find(hasLegalIdentity) ||
    fallback[1];
  return { ...profile, consents: buildConsents(profile) };
}
